import React, { useMemo } from 'react';
import { Card } from '../ui';
import { useCop } from '../CopApp';

// Builds a company_json campaign object from the configured targeting rules.
// Mirrors the default rules documented in prompts/files/company_json.md. Site
// IDs / hashes stay ALL_CAPS placeholders — never invented (see rules.md).
export function buildCompanyJson(rules, selected) {
	const campaign = { name: `${selected.segment_name} Campaign`, siteID: 'SITE_ID', hash: 'CAMPAIGN_HASH' };
	const list = (v) => v.split(',').map((s) => s.trim()).filter(Boolean);

	if (rules.device === 'mobile') campaign.mobile = true;
	if (rules.device === 'desktop') campaign.desktop = true;
	if (rules.testing) campaign.testing = true;
	if (rules.loggedState === 'logged_in') campaign.logged_in = true;
	if (rules.loggedState === 'logged_out') campaign.logged_out = true;
	if (rules.visitor === 'new') campaign.new_visitor = true;
	if (rules.visitor === 'return') campaign.return_visitor = true;
	if (rules.customer === 'new') campaign.new_customer = true;
	if (rules.customer === 'return') campaign.return_customer = true;
	if (rules.pages) campaign.pages = list(rules.pages);
	if (rules.suppressedPages) campaign.suppressed_pages = list(rules.suppressedPages);
	if (rules.requiredElements) campaign.required_elements = list(rules.requiredElements);
	if (rules.requiredCookies) campaign.required_cookies = list(rules.requiredCookies);
	if (rules.suppressCookies) campaign.suppress_cookies = list(rules.suppressCookies);
	if (rules.countries) campaign.countries = list(rules.countries).map((c) => c.toLowerCase());
	if (rules.states) campaign.states = list(rules.states).map((s) => s.toUpperCase());
	if (rules.trafficSource) campaign.traffic_source = list(rules.trafficSource);
	if (rules.minSubtotal !== '') campaign.min_subtotal = Number(rules.minSubtotal);
	if (rules.maxSubtotal !== '') campaign.max_subtotal = Number(rules.maxSubtotal);
	const pv = {};
	if (rules.pageVisitsMin !== '') pv.min = Number(rules.pageVisitsMin);
	if (rules.pageVisitsMax !== '') pv.max = Number(rules.pageVisitsMax);
	if (Object.keys(pv).length) campaign.page_visits = pv;
	if (rules.isEntry) campaign.is_entry = true;
	if (rules.isAfter) campaign.is_after = `${rules.isAfter}T00:00:00-07:00`;
	if (rules.isBefore) campaign.is_before = `${rules.isBefore}T00:00:00-07:00`;
	if (Number(rules.trafficPercent) < 100) campaign.traffic_percent = Number((Number(rules.trafficPercent) / 100).toFixed(2));

	return { campaigns: { view: [campaign] } };
}

function Field({ label, children, span }) {
	return (
		<label className={`cop-form-field${span ? ' cop-form-span' : ''}`}>
			<span>{label}</span>
			{children}
		</label>
	);
}

export default function CampaignRules({ rules, setRules, selected }) {
	const { showToast } = useCop();
	const set = (key) => (e) => setRules((r) => ({ ...r, [key]: e.target.value }));
	const toggle = (key) => () => setRules((r) => ({ ...r, [key]: !r[key] }));

	const json = useMemo(() => JSON.stringify(buildCompanyJson(rules, selected), null, 2), [rules, selected]);

	const copyJson = () => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(json).then(() => showToast('company_json copied'));
		} else {
			showToast('Copy not available');
		}
	};

	return (
		<div className="cop-grid cop-builder-rules">
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				<Card title="Visitor & Customer" icon="◐" right="company_json targeting rules">
					<div className="cop-form-grid">
						<Field label="Device">
							<select className="cop-select" value={rules.device} onChange={set('device')}>
								<option value="all">All devices</option>
								<option value="mobile">Mobile only</option>
								<option value="desktop">Desktop only</option>
							</select>
						</Field>
						<Field label="Login state">
							<select className="cop-select" value={rules.loggedState} onChange={set('loggedState')}>
								<option value="any">Any</option>
								<option value="logged_in">Logged in</option>
								<option value="logged_out">Logged out</option>
							</select>
						</Field>
						<Field label="Visitor">
							<select className="cop-select" value={rules.visitor} onChange={set('visitor')}>
								<option value="any">Any</option>
								<option value="new">New visitor</option>
								<option value="return">Return visitor</option>
							</select>
						</Field>
						<Field label="Customer">
							<select className="cop-select" value={rules.customer} onChange={set('customer')}>
								<option value="any">Any</option>
								<option value="new">New customer</option>
								<option value="return">Return customer</option>
							</select>
						</Field>
					</div>
				</Card>

				<Card title="Pages & Geography" icon="◎">
					<div className="cop-form-grid">
						<Field label="Pages (comma-separated)" span>
							<input className="cop-input" value={rules.pages} onChange={set('pages')} placeholder="/cart, /product" />
						</Field>
						<Field label="Suppressed pages" span>
							<input className="cop-input" value={rules.suppressedPages} onChange={set('suppressedPages')} placeholder="/cart/checkout" />
						</Field>
						<Field label="Countries">
							<input className="cop-input" value={rules.countries} onChange={set('countries')} placeholder="us, ca" />
						</Field>
						<Field label="States">
							<input className="cop-input" value={rules.states} onChange={set('states')} placeholder="CA, NV" />
						</Field>
						<Field label="Traffic source" span>
							<input className="cop-input" value={rules.trafficSource} onChange={set('trafficSource')} placeholder="Direct, email" />
						</Field>
					</div>
				</Card>

				<Card title="Cart & Engagement" icon="▦">
					<div className="cop-form-grid">
						<Field label="Min subtotal ($)">
							<input className="cop-input" type="number" min="0" value={rules.minSubtotal} onChange={set('minSubtotal')} placeholder="100" />
						</Field>
						<Field label="Max subtotal ($)">
							<input className="cop-input" type="number" min="0" value={rules.maxSubtotal} onChange={set('maxSubtotal')} placeholder="—" />
						</Field>
						<Field label="Page visits — min">
							<input className="cop-input" type="number" min="0" value={rules.pageVisitsMin} onChange={set('pageVisitsMin')} placeholder="0" />
						</Field>
						<Field label="Page visits — max">
							<input className="cop-input" type="number" min="0" value={rules.pageVisitsMax} onChange={set('pageVisitsMax')} placeholder="—" />
						</Field>
						<div className="cop-config-row cop-form-span">
							<span className="k">Entry page only (is_entry)</span>
							<button className={`cop-toggle${rules.isEntry ? ' on' : ''}`} onClick={toggle('isEntry')} aria-label="toggle is_entry" />
						</div>
					</div>
				</Card>

				<Card title="Technical & Schedule" icon="⚙">
					<div className="cop-form-grid">
						<Field label="Required elements" span>
							<input className="cop-input" value={rules.requiredElements} onChange={set('requiredElements')} placeholder="#mini-cart, .cart-summary" />
						</Field>
						<Field label="Required cookies">
							<input className="cop-input" value={rules.requiredCookies} onChange={set('requiredCookies')} placeholder="usi_subtotal" />
						</Field>
						<Field label="Suppress cookies">
							<input className="cop-input" value={rules.suppressCookies} onChange={set('suppressCookies')} placeholder="usi_entry_email" />
						</Field>
						<Field label="Active after">
							<input className="cop-input" type="date" value={rules.isAfter} onChange={set('isAfter')} />
						</Field>
						<Field label="Active before">
							<input className="cop-input" type="date" value={rules.isBefore} onChange={set('isBefore')} />
						</Field>
						<Field label={`Traffic percent — ${rules.trafficPercent}%`} span>
							<input type="range" min="1" max="100" value={rules.trafficPercent} onChange={set('trafficPercent')} />
						</Field>
						<div className="cop-config-row cop-form-span">
							<span className="k">Testing mode (usi_enable)</span>
							<button className={`cop-toggle${rules.testing ? ' on' : ''}`} onClick={toggle('testing')} aria-label="toggle testing" />
						</div>
					</div>
				</Card>
			</div>

			<Card title="company_json preview" icon="{ }" right={<button className="cop-btn small" onClick={copyJson}>Copy</button>}>
				<pre className="cop-json-preview">{json}</pre>
				<div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>
					Rules placed in <span className="cop-mono">company_json</span> are visible in the Chrome Extension side panel, so missing
					requirements are diagnosable without reading code. Site IDs and hashes are placeholders until the campaign is provisioned.
				</div>
			</Card>
		</div>
	);
}
